import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { FaBriefcase, FaMapMarkerAlt, FaDollarSign, FaCertificate, FaCalendarAlt } from 'react-icons/fa';
import SEOHead from '../components/SEOHead';
import { ROLE_JOB_QUERIES } from '../utils/roleJobQueries';

interface RoleData {
  name: string;
  shortName: string;
  slug: string;
  level: string;
  description: string;
  responsibilities: string[];
  eventTypes: string[];
  skills: string[];
  certifications: string[];
  topMarkets: string[];
  careerPath: string;
}

const ROLES: Record<string, RoleData> = {
  'audio-engineer': {
    name: 'Audio Engineer (A1)',
    shortName: 'Audio Engineer',
    slug: 'audio-engineer',
    level: 'A1 — Lead Audio',
    description: 'Audio Engineers (A1) are the lead audio professionals on any live event. They design and operate the front-of-house (FOH) sound system, manage monitor mixes, and ensure every audience member hears crystal-clear audio. From arena concerts to corporate keynotes, the A1 is responsible for the entire audio experience.',
    responsibilities: [
      'Design and operate front-of-house (FOH) sound systems',
      'Program and manage monitor mixes for performers',
      'Select and position microphones, speakers, and signal processors',
      'Troubleshoot audio issues in real-time during live shows',
      'Coordinate with production teams on audio requirements',
      'Manage RF frequencies for wireless microphones and IEMs',
      'Create and maintain input lists, patch sheets, and system diagrams',
    ],
    eventTypes: ['Concerts & tours', 'Corporate keynotes', 'Award shows', 'Festivals', 'Theater', 'Broadcast/streaming'],
    skills: ['Digital console operation (Yamaha CL/QL, DiGiCo, Avid)', 'System tuning & alignment (SMAART, SIM)', 'RF coordination', 'Dante/AES67 networking', 'Monitor mixing (wedges & IEMs)', 'Signal flow & gain structure'],
    certifications: ['Dante Level 1-3', 'SynAudCon training', 'AVIXA CTS (optional)', 'Manufacturer certifications (L-Acoustics, d&b)'],
    topMarkets: ['Las Vegas', 'Los Angeles', 'Nashville', 'New York', 'Chicago', 'Austin'],
    careerPath: 'A2 (Audio Tech) → A1 (Audio Engineer) → System Engineer → Audio Director',
  },
  'audio-technician': {
    name: 'Audio Technician (A2)',
    shortName: 'Audio Tech',
    slug: 'audio-technician',
    level: 'A2 — Audio Support',
    description: 'Audio Technicians (A2) work alongside the A1 engineer to set up, patch, and maintain audio systems. They handle cable runs, microphone placement, stage patching, and assist with RF management. The A2 role is the essential hands-on backbone of any audio department.',
    responsibilities: [
      'Run and dress audio cables (XLR, NL4, Cat6)',
      'Set up and strike microphones, DI boxes, and stands',
      'Patch stage boxes and manage signal routing',
      'Assist with wireless RF scanning and frequency coordination',
      'Maintain and inventory audio equipment',
      'Operate comms systems and monitor stage audio',
      'Support A1 during soundcheck and live show',
    ],
    eventTypes: ['Concerts & tours', 'Corporate events', 'Festivals', 'Theater', 'Convention sessions', 'Broadcast'],
    skills: ['Cable management & soldering', 'Stage patching & signal flow', 'RF scanning (Shure WWB, Sennheiser WSM)', 'Basic console operation', 'Comms systems (Clear-Com, RTS)', 'Equipment maintenance'],
    certifications: ['Dante Level 1', 'OSHA 10 (recommended)', 'Manufacturer training (Shure, Sennheiser)'],
    topMarkets: ['Las Vegas', 'Los Angeles', 'Nashville', 'New York', 'Chicago', 'Orlando'],
    careerPath: 'Stagehand → A2 (Audio Tech) → A1 (Audio Engineer) → System Engineer',
  },
  'video-engineer': {
    name: 'Video Engineer (V1)',
    shortName: 'Video Engineer',
    slug: 'video-engineer',
    level: 'V1 — Lead Video',
    description: 'Video Engineers (V1) lead the video department for live events. They operate video switchers, manage LED wall content, run media servers, and ensure seamless playback during shows. From IMAG at concerts to multi-screen corporate presentations, the V1 owns the visual experience.',
    responsibilities: [
      'Operate video switchers (Barco E2/S3, Analog Way, Ross)',
      'Program and run media servers (disguise, Watchout, Resolume)',
      'Manage LED wall processors and content mapping',
      'Coordinate camera feeds for IMAG (image magnification)',
      'Create show files, cue lists, and playback sequences',
      'Troubleshoot video signal chain issues in real-time',
      'Interface with lighting and audio teams on show integration',
    ],
    eventTypes: ['Corporate general sessions', 'Award shows', 'Concerts (IMAG)', 'Trade shows', 'Broadcast', 'Virtual/hybrid events'],
    skills: ['Barco E2/S3 or Analog Way switching', 'Media server programming (disguise, Watchout)', 'LED processor configuration (Novastar, Brompton)', 'NDI/SDI signal routing', 'Color calibration', 'Show control integration'],
    certifications: ['Barco certified operator', 'disguise certified', 'AVIXA CTS', 'Dante Level 1-2'],
    topMarkets: ['Las Vegas', 'Los Angeles', 'New York', 'Orlando', 'Chicago', 'San Francisco'],
    careerPath: 'V2 (Video Tech) → V1 (Video Engineer) → Technical Director → Video Director',
  },
  'video-technician': {
    name: 'Video Technician (V2)',
    shortName: 'Video Tech',
    slug: 'video-technician',
    level: 'V2 — Video Support',
    description: 'Video Technicians (V2) handle the physical setup and support of video systems. They build LED walls, run video cables, set up projection screens, operate cameras for IMAG, and assist the V1 engineer during shows. The V2 is where most video careers start.',
    responsibilities: [
      'Build and strike LED video walls (tile installation, cabling)',
      'Run SDI, HDMI, and fiber video cables',
      'Set up projection screens, projectors, and monitors',
      'Operate cameras for IMAG and recording',
      'Assist with video signal testing and troubleshooting',
      'Maintain and transport video equipment',
      'Support V1 during show execution',
    ],
    eventTypes: ['Corporate events', 'Trade shows', 'Concerts', 'Conventions', 'Hybrid/virtual events', 'Sports events'],
    skills: ['LED wall assembly (ROE, Absen)', 'Camera operation', 'SDI/HDMI/fiber cabling', 'Projection setup & alignment', 'Basic switching operation', 'Equipment inventory management'],
    certifications: ['OSHA 10', 'Manufacturer LED training', 'Dante Level 1 (helpful)'],
    topMarkets: ['Las Vegas', 'Los Angeles', 'Orlando', 'New York', 'Chicago', 'Dallas'],
    careerPath: 'Stagehand → V2 (Video Tech) → Camera Operator → V1 (Video Engineer)',
  },
  'lighting-designer': {
    name: 'Lighting Designer (L1)',
    shortName: 'Lighting Designer',
    slug: 'lighting-designer',
    level: 'L1 — Lead Lighting',
    description: 'Lighting Designers (L1) create the visual atmosphere for live events through creative and technical lighting design. They program consoles, design plots, and call cues during shows. The L1 transforms a dark room into an immersive experience.',
    responsibilities: [
      'Design lighting plots and fixture layouts',
      'Program lighting consoles (grandMA, ETC Eos, Hog 4)',
      'Create and manage cue lists for live show execution',
      'Select and specify fixtures, gobos, and color palettes',
      'Coordinate with set designers and video teams',
      'Manage dimmer racks and power distribution',
      'Direct focus calls and oversee L2 crew',
    ],
    eventTypes: ['Concerts & tours', 'Theater', 'Corporate galas', 'Award shows', 'Festivals', 'Brand activations'],
    skills: ['grandMA2/3 programming', 'ETC Eos family', 'Vectorworks/WYSIWYG for pre-visualization', 'DMX/sACN/Art-Net networking', 'Color theory & design principles', 'Power distribution & electrical safety'],
    certifications: ['MA Lighting certified', 'ETC certified', 'ETCP Electrician (optional)', 'AVIXA CTS'],
    topMarkets: ['Las Vegas', 'Los Angeles', 'Nashville', 'New York', 'Chicago', 'Atlanta'],
    careerPath: 'L2 (Lighting Tech) → L1 (Lighting Designer) → Lighting Director → Production Designer',
  },
  'lighting-technician': {
    name: 'Lighting Technician (L2)',
    shortName: 'Lighting Tech',
    slug: 'lighting-technician',
    level: 'L2 — Lighting Support',
    description: 'Lighting Technicians (L2) handle the physical work of a lighting department — hanging fixtures, running cable, focusing lights, operating followspots, and supporting the L1 designer. This is the most common entry point into lighting careers.',
    responsibilities: [
      'Hang, circuit, and focus lighting fixtures',
      'Run DMX, power, and data cables',
      'Operate followspots during live shows',
      'Gel and accessorize fixtures per the lighting plot',
      'Assist with dimmer rack setup and power distribution',
      'Maintain and inventory lighting equipment',
      'Strike and pack lighting rigs after events',
    ],
    eventTypes: ['Concerts', 'Corporate events', 'Theater', 'Trade shows', 'Festivals', 'Church events'],
    skills: ['Fixture hanging & rigging (c-clamps, safety cables)', 'Followspot operation', 'DMX cabling & troubleshooting', 'Basic console operation', 'Electrical safety & load calculations', 'Gel cutting & color management'],
    certifications: ['OSHA 10', 'ETCP Electrician (advanced)', 'Fall protection training'],
    topMarkets: ['Las Vegas', 'Los Angeles', 'Nashville', 'New York', 'Chicago', 'Dallas'],
    careerPath: 'Stagehand → L2 (Lighting Tech) → Followspot Operator → L1 (Lighting Designer)',
  },
  'stagehand': {
    name: 'Stagehand',
    shortName: 'Stagehand',
    slug: 'stagehand',
    level: 'General Labor',
    description: 'Stagehands support load-in, load-out, set construction, and other physical production tasks. Requirements vary by venue and call, so review the listing scope, safety requirements, schedule, and experience expectations.',
    responsibilities: [
      'Load in and load out equipment (cases, truss, staging)',
      'Build and strike staging, set pieces, and scenic elements',
      'Push cases and operate dollies, carts, and forklifts',
      'Assist audio, video, and lighting departments as needed',
      'Follow direction from department heads and crew chiefs',
      'Maintain a safe work environment (PPE, clear paths)',
      'Work extended hours during multi-day events',
    ],
    eventTypes: ['Concerts & tours', 'Conventions', 'Corporate events', 'Festivals', 'Theater', 'Sports events', 'Trade shows'],
    skills: ['Physical fitness & stamina', 'Forklift operation (certified preferred)', 'Basic tool proficiency', 'Team communication', 'Safety awareness (PPE, lift techniques)', 'Ability to read ground plans'],
    certifications: ['OSHA 10 (strongly recommended)', 'Forklift certification', 'Fall protection (for rigging assist)', 'First aid/CPR (bonus)'],
    topMarkets: ['Las Vegas', 'Los Angeles', 'New York', 'Chicago', 'Orlando', 'Nashville', 'Dallas', 'Atlanta'],
    careerPath: 'Stagehand → Department-specific A2/V2/L2 → Lead/A1/V1/L1 → Crew Chief → Production Manager',
  },
  'rigger': {
    name: 'Rigger',
    shortName: 'Rigger',
    slug: 'rigger',
    level: 'Specialty',
    description: 'Riggers handle truss, motors, and scenic elements in arenas, theaters, and outdoor stages. This is safety-critical work; verify the venue, certification, insurance, equipment, and supervision requirements for each call.',
    responsibilities: [
      'Read and interpret rigging plots and weight calculations',
      'Rig truss, chain motors, and bridle points from steel',
      'Calculate load capacities, bridle angles, and dead hangs',
      'Operate chain hoists and motor controllers',
      'Perform high-steel climbing and overhead work',
      'Inspect rigging hardware (shackles, slings, spansets)',
      'Ensure compliance with OSHA and venue rigging regulations',
    ],
    eventTypes: ['Arena concerts & tours', 'Festivals', 'Theater (fly systems)', 'Corporate events (large-scale)', 'Broadcast', 'Cirque-style shows'],
    skills: ['High-steel climbing', 'Load calculation & bridle math', 'Chain motor operation', 'Knot tying (bowline, clove hitch)', 'Fall protection systems', 'Truss assembly & inspection'],
    certifications: ['ETCP Certified Rigger — Arena', 'ETCP Certified Rigger — Theater', 'OSHA 30', 'Fall protection certified', 'First aid/CPR'],
    topMarkets: ['Las Vegas', 'Los Angeles', 'New York', 'Chicago', 'Nashville', 'Orlando'],
    careerPath: 'Stagehand → Rigger Assistant → Rigger → Head Rigger → Rigging Designer',
  },
  'stage-manager': {
    name: 'Stage Manager',
    shortName: 'Stage Manager',
    slug: 'stage-manager',
    level: 'Management',
    description: 'Stage Managers are the central coordinators of a live event. They call cues, manage schedules, communicate between departments, and ensure the show runs smoothly from start to finish. A great stage manager is the glue that holds a production together.',
    responsibilities: [
      'Call lighting, audio, video, and scenic cues during shows',
      'Create and distribute show schedules, call sheets, and cue sheets',
      'Coordinate between all production departments',
      'Manage rehearsal schedules and run-throughs',
      'Communicate with talent, crew, and venue management',
      'Maintain prompt book with all cues and blocking',
      'Troubleshoot scheduling conflicts and last-minute changes',
    ],
    eventTypes: ['Theater', 'Corporate events', 'Award shows', 'Concerts', 'Broadcast', 'Festivals'],
    skills: ['Cue calling', 'Schedule management', 'Clear radio communication', 'Conflict resolution', 'Multi-department coordination', 'Documentation (cue sheets, blocking)'],
    certifications: ['No formal certifications required', 'AEA membership (theater-specific)', 'First aid/CPR (recommended)'],
    topMarkets: ['New York', 'Los Angeles', 'Las Vegas', 'Nashville', 'Chicago', 'Atlanta'],
    careerPath: 'Production Assistant → Assistant Stage Manager → Stage Manager → Production Manager',
  },
  'led-technician': {
    name: 'LED Technician',
    shortName: 'LED Tech',
    slug: 'led-technician',
    level: 'Specialty',
    description: 'LED Technicians build, configure, and maintain LED video walls for concerts, corporate events, trade shows, and broadcast environments. Equipment, processor, mapping, and safety requirements vary by call.',
    responsibilities: [
      'Assemble and strike LED wall panels (tile-by-tile installation)',
      'Configure LED processors (Novastar, Brompton, Colorlight)',
      'Map content to LED surfaces (curved, non-standard shapes)',
      'Troubleshoot pixel mapping, color calibration, and data issues',
      'Run data and power cables for LED systems',
      'Perform on-site maintenance and panel swaps',
      'Coordinate with video engineers on content delivery',
    ],
    eventTypes: ['Corporate events', 'Concerts & tours', 'Trade shows', 'Broadcast studios', 'Brand activations', 'Esports events'],
    skills: ['LED panel assembly (ROE, Absen, Unilumin)', 'Novastar/Brompton processor configuration', 'Pixel mapping & calibration', 'Data networking (fiber, Cat6)', 'Basic structural assembly', 'Content formatting & resolution management'],
    certifications: ['Manufacturer certifications (ROE, Brompton)', 'OSHA 10', 'Dante Level 1 (helpful)'],
    topMarkets: ['Las Vegas', 'Los Angeles', 'New York', 'Orlando', 'Chicago', 'San Francisco'],
    careerPath: 'V2 (Video Tech) → LED Technician → LED Engineer → Video Engineer (V1)',
  },
  'camera-operator': {
    name: 'Camera Operator',
    shortName: 'Camera Op',
    slug: 'camera-operator',
    level: 'Specialty',
    description: 'Camera Operators capture live event footage for IMAG (image magnification), broadcast, streaming, and recording. From arena concert IMAG to corporate webcasts, camera operators blend technical skill with creative framing to tell the story of the event.',
    responsibilities: [
      'Operate broadcast and IMAG cameras (handheld, tripod, jib)',
      'Follow director cues for shot selection and framing',
      'Set up and position cameras according to the camera plot',
      'Manage camera settings (iris, focus, white balance, shading)',
      'Run camera cables (triax, fiber, SDI)',
      'Communicate with video director via comms',
      'Adapt framing for different content (speaker, performance, audience)',
    ],
    eventTypes: ['Concerts (IMAG)', 'Corporate events', 'Broadcast/streaming', 'Sports events', 'Award shows', 'Esports'],
    skills: ['Broadcast camera operation', 'Framing & composition', 'Following director cues', 'Triax/fiber/SDI cabling', 'Camera shading & color matching', 'Jib/crane/Steadicam operation (advanced)'],
    certifications: ['No formal certifications required', 'Manufacturer training (Sony, Grass Valley)', 'Steadicam certification (specialty)'],
    topMarkets: ['Los Angeles', 'New York', 'Las Vegas', 'Atlanta', 'Nashville', 'Chicago'],
    careerPath: 'V2 (Video Tech) → Camera Operator → Camera Shader → Video Director',
  },
  'production-assistant': {
    name: 'Production Assistant',
    shortName: 'Production Assistant',
    slug: 'production-assistant',
    level: 'Entry Level',
    description: 'Production Assistants support logistics, deliveries, credentials, errands, and department coordination. Entry requirements and responsibilities vary by employer and production.',
    responsibilities: [
      'Coordinate logistics (deliveries, credentials, parking)',
      'Assist production manager with scheduling and communications',
      'Run errands and manage supplies',
      'Manage check-in and credentialing for crew and talent',
      'Distribute call sheets, maps, and production documents',
      'Support any department that needs extra hands',
      'Maintain clean and organized production office/backstage',
    ],
    eventTypes: ['Corporate events', 'Concerts', 'Festivals', 'Film/TV production', 'Trade shows', 'Award shows'],
    skills: ['Organization & time management', 'Clear communication (radio, in-person)', 'Problem solving under pressure', 'Basic computer skills (Google Sheets, email)', 'Driving & logistics coordination', 'Physical stamina'],
    certifications: ['No formal certifications required', 'Valid driver\'s license', 'OSHA 10 (helpful)', 'First aid/CPR (bonus)'],
    topMarkets: ['Los Angeles', 'New York', 'Las Vegas', 'Atlanta', 'Nashville', 'Chicago', 'Orlando'],
    careerPath: 'Production Assistant → Stage Manager / Department A2/V2/L2 → Department Lead → Production Manager',
  },
};

const RolePage: React.FC = () => {
  const { role } = useParams<{ role: string }>();

  if (!role || !ROLES[role]) {
    return <Navigate to="/jobs" replace />;
  }

  const r = ROLES[role];
  const allRoles = Object.values(ROLES);
  const jobQuery = ROLE_JOB_QUERIES[r.slug] || {
    param: 'search',
    value: r.shortName,
    ctaLabel: undefined,
  };

  return (
    <>
      <SEOHead
        title={`${r.name} Gigs — Find ${r.shortName} Jobs | StageCrew`}
        description={`Browse ${r.shortName} role tasks, skills, and current live-event listings. Rates vary by market, employer, experience, and scope; verify each listing.`}
        url={`https://stagecrew.app/roles/${r.slug}`}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="bg-white/20 text-sm font-bold px-3 py-1 rounded-full">{r.level}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4">{r.name} Gigs</h1>
          <p className="text-lg md:text-xl opacity-90 max-w-2xl mb-6">{r.description}</p>
          <div className="flex flex-wrap gap-4">
            <Link
              to={`/jobs?${jobQuery.param}=${encodeURIComponent(jobQuery.value)}`}
              className="bg-white text-blue-700 px-8 py-3 rounded-lg font-bold hover:bg-blue-50 transition-colors"
            >
              {jobQuery.ctaLabel || `Find ${r.shortName} Gigs`}
            </Link>
            <Link
              to="/for-employers"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-white/10 transition-colors"
            >
              Hire {r.shortName}s
            </Link>
          </div>
        </div>
      </section>

      {/* Role guidance */}
      <section className="bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 text-center shadow-sm">
            <FaDollarSign className="text-green-600 text-xl mx-auto mb-2" />
            <div className="text-xl font-bold text-gray-900">Rates Vary</div>
            <div className="text-sm text-gray-500">Verify Each Listing</div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center shadow-sm">
            <FaMapMarkerAlt className="text-blue-600 text-xl mx-auto mb-2" />
            <div className="text-xl font-bold text-gray-900">Current Listings</div>
            <div className="text-sm text-gray-500">Availability Changes</div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center shadow-sm">
            <FaCalendarAlt className="text-purple-600 text-xl mx-auto mb-2" />
            <div className="text-xl font-bold text-gray-900">Role-Specific</div>
            <div className="text-sm text-gray-500">Event Requirements</div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center shadow-sm">
            <FaBriefcase className="text-orange-600 text-xl mx-auto mb-2" />
            <div className="text-xl font-bold text-gray-900">Employer Review</div>
            <div className="text-sm text-gray-500">Hiring Decisions</div>
          </div>
        </div>
      </section>

      {/* Responsibilities */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">What Does a {r.shortName} Do?</h2>
          <ul className="space-y-3">
            {r.responsibilities.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-bold mt-0.5">
                  {i + 1}
                </span>
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Skills & Certifications */}
      <section className="bg-gray-50 py-12 px-4">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Required Skills</h2>
            <div className="flex flex-wrap gap-2">
              {r.skills.map((skill, i) => (
                <span key={i} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                  {skill}
                </span>
              ))}
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              <FaCertificate className="inline text-yellow-500 mr-2" />
              Certifications
            </h2>
            <ul className="space-y-2">
              {r.certifications.map((cert, i) => (
                <li key={i} className="flex items-center gap-2 text-gray-700">
                  <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full flex-shrink-0" />
                  {cert}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Event Types */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Common Event Types</h2>
          <div className="flex flex-wrap gap-3">
            {r.eventTypes.map((type, i) => (
              <span key={i} className="px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg text-sm font-medium">
                {type}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Market guides */}
      <section className="bg-gray-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Market Guides for {r.shortName}s</h2>
          <p className="text-gray-600 mb-4">These are city guides, not a ranking or promise of current inventory.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {r.topMarkets.map((market) => {
              const slug = market.toLowerCase().replace(/\s+/g, '-');
              return (
                <Link
                  key={market}
                  to={`/gigs/${slug}`}
                  className="flex items-center gap-2 px-4 py-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow text-gray-900 font-medium"
                >
                  <FaMapMarkerAlt className="text-blue-600 flex-shrink-0" />
                  {market}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Career Path */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Career Path</h2>
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
            <p className="text-lg text-gray-700 font-medium">{r.careerPath}</p>
          </div>
        </div>
      </section>

      {/* Browse Other Roles */}
      <section className="bg-gray-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Browse All Crew Roles</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {allRoles.map((otherRole) => (
              <Link
                key={otherRole.slug}
                to={`/roles/${otherRole.slug}`}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  otherRole.slug === role
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-700 shadow-sm'
                }`}
              >
                {otherRole.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-blue-600 text-white py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Find {r.shortName} Gigs?</h2>
          <p className="text-lg opacity-90 mb-8">
            Sign up free and get matched with {r.shortName.toLowerCase()} jobs in your market. Optional Automation Early Access is $9/month and works only where the application path supports it.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link
              to="/register?role=worker"
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold hover:bg-blue-50 transition-colors"
            >
              Sign Up Free
            </Link>
            <Link
              to="/jobs"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-white/10 transition-colors"
            >
              Browse All Gigs
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default RolePage;
